import React, { useState } from "react";
import { Container, Grid } from "@mui/material";
import useSWRMutation from "swr/mutation";
import { useTranslation } from "react-i18next";

import BackgroundImage from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL, downloadFile, uploadFile } from "@utils/utilsApi";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import CardHomeCst from "@components/CardHomeCst";
import { useMenu, useNavigaionRoutes } from "@hooks/useNavigation";
import { useAuth } from "@context/auth";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useResetFilters } from "@hooks/useFiltersProvider";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

// const navigazione = [
// 	{
// 	  text: "Navigazione",
// 	  destination: "#header",
// 	},
// 	{
// 	  text: "Contatori",
// 	  destination: "#contatori",
// 	},
// 	{
// 	  text: "Lista colleghi",
// 	  destination: "#lista-colleghi",
// 	},
// ];

const Skills: React.FC = () => {
  // const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(["skills-page", "common"]);

  const [configData, setConfigData] = useState<
    ActionUploadDownloadIterface | undefined
  >();

  const menuItem = useMenu();
  const navigation = useNavigaionRoutes();

  const { trigger } = useSWRMutation(
    `/mediamanager/1/validation/upload?corporateId=1`,
    (url: string, arg: { arg: { fileName: string } }) =>
      fetcher(`${url}&fileName=${arg.arg.fileName}`, MethodType.GET),
  );
  const { trigger: triggerDownload } = useSWRMutation(
    `/mediamanager/1/validation/download?corporateId=1`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const skillsSection = [
    "skills.industry.retrieve",
    "skills.profession.retrieve",
    "skills.knowledge.area.retrieve",
    "skills.capabilities.retrieve",
    "skills.job.knowledge.suggestion.read",
    "skills.validation-autotagging",
  ];

  useResetFilters();

  let skillsMenu = [];
  if (menuItem.length > 0) {
    skillsMenu = navigation
      .find((el) => el.to === "skills")
      ?.children?.filter((el: { section: string }) => {
        return (
          user?.permissions?.includes(el.section) &&
          skillsSection?.includes(el.section)
        );
      });
  }

  const handleUpload = (title: string) => {
    setOpen(true);

    switch (title) {
      case "tree":
        setConfigData(treeConfig);
        break;
      case "skills":
        setConfigData(skillsConfig);
        break;
      case "job":
        setConfigData(jobConfig);
        break;

      case "validation-autotagging":
        setConfigData(validationAutotagging);
        break;

      default:
        break;
    }
  };

  const treeConfig: ActionUploadDownloadIterface | undefined = {
    types: ["upload", "download", "template"],
    type: "upload",
    permission: "skillprofession.tree.download",
    permissionUpload: "skillprofession.tree.save",
    accept: "text/csv",
    icon: "upload",
    labelTranslate: "Tree",
    label: { en: "", it: "" },
    actions: [],
    service: ServicesURL.skills,
    tooltipFile: true,
    tooltipFileIta: "CSV_MESTIERI_E_PROFESSIONI.pdf",
    tooltipFileEn: "CSV_SKILLS_AND_PROFESSIONS.pdf",
    onDownload: (el: any) => {
      if (el === "template") {
        downloadFile(`${ServicesURL.skills}/tree/template` || "");
      } else {
        downloadFile(`${ServicesURL.skills}/tree` || "");
      }
    },
    onUpload: (formData: FormData) =>
      uploadFile(`${ServicesURL.skills}/tree`, formData),
  };

  const skillsConfig: ActionUploadDownloadIterface | undefined = {
    types: ["upload", "download", "template"],
    type: "upload",
    permission: "skillprofession.skills.download",
    permissionUpload: "skillprofession.skills.save",
    accept: "text/csv",
    icon: "upload",
    labelTranslate: "Skills",
    label: { en: "", it: "" },
    actions: [],
    service: ServicesURL.skills,
    tooltipFile: true,
    tooltipFileIta: "CSV_MESTIERI_E_PROFESSIONI.pdf",
    tooltipFileEn: "CSV_SKILLS_AND_PROFESSIONS.pdf",
    onDownload: (el: any) => {
      if (el === "template") {
        downloadFile(`${ServicesURL.skills}/skills/template` || "");
      } else {
        downloadFile(`${ServicesURL.skills}/skills` || "");
      }
    },
    onUpload: (formData: FormData) =>
      uploadFile(`${ServicesURL.skills}/skills`, formData),
  };

  const jobConfig: ActionUploadDownloadIterface | undefined = {
    types: ["upload", "download", "template"],
    type: "upload",
    permission: "skillprofession.skilljobrelation.download",
    permissionUpload: "skillprofession.skilljobrelation.save",
    accept: "text/csv",
    icon: "upload",
    labelTranslate: "Job",
    label: { en: "", it: "" },
    actions: [],
    service: ServicesURL.skills,
    tooltipFile: true,
    tooltipFileIta: "CSV_MESTIERI_E_PROFESSIONI.pdf",
    tooltipFileEn: "CSV_SKILLS_AND_PROFESSIONS.pdf",
    onDownload: (el: any) => {
      if (el === "template") {
        downloadFile(`${ServicesURL.skills}/skills/jobs/template` || "");
      } else {
        downloadFile(`${ServicesURL.skills}/skills/relation` || "");
      }
    },
    onUpload: (formData: FormData) =>
      uploadFile(`${ServicesURL.skills}/skills/jobs`, formData),
  };

  const validationAutotagging: ActionUploadDownloadIterface | undefined = {
    types: ["upload", "download"],
    type: "upload",
    permission: "skills.validation-autotagging",
    permissionUpload: "skills.validation-autotagging",
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    icon: "upload",
    labelTranslate: `${t("skills.validation-autotagging.title", {
      ns: "menu",
    })}`,
    label: { en: "", it: "" },
    actions: [],
    service: ServicesURL.skills,
    tooltipFile: false,
    // tooltipFileIta: "CSV_MESTIERI_E_PROFESSIONI.pdf",
    // tooltipFileEn: "CSV_SKILLS_AND_PROFESSIONS.pdf",
    onDownload: async (el: any) => {
      const response: { private_url: string; public_url: string } =
        await triggerDownload();
      downloadFile(`${response.public_url}` || "");
    },

    onUpload: async (formData: FormData) => {
      let file = formData.get("file") as File;
      try {
        const response: { url: string; fileId: string } = await trigger({
          fileName: file.name,
        });
        const fileBlob = new Blob([file], {
          type: file.type,
        });
        await uploadFile(response.url, fileBlob, MethodType.PUT, file.type);
      } catch (error) {
        console.error("error", error);
      }
    },
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-skills.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <Grid container spacing={3}>
          {skillsMenu.map((card: any, index: number) => (
            <Grid key={index} item xs={12} md={6} xl={4}>
              <CardHomeCst
                image={card.image}
                title={card.text}
                to={
                  !["validation-autotagging"].includes(card.to)
                    ? card.to
                    : undefined
                }
                description={card.description}
                secondaryBtnLabel={card.buttonLabel}
                uploadBtn={["validation-autotagging"].includes(card.to)}
                handleUpload={() => handleUpload(card.to)}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
      <ModalDownloadUploadCst
        open={!!open}
        setOpen={() => {
          setOpen(false);
        }}
        title={configData && configData.labelTranslate}
        data={configData}
      />
    </AnimationFadeIn>
  );
};

export default Skills;
